import { useEffect, useId, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  DESKTOP_DOCK_ITEMS,
  isDockDestinationActive,
  type DockDestination,
} from './navigation';

interface MobilePrimaryNavProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Mobile overflow sheet listing all primary dock destinations.
 * Replaces the former More menu — same destinations as the dock, not a secondary IA.
 */
export function MobilePrimaryNav({ open, onClose }: MobilePrimaryNavProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLElement>('a, button');
    first?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="more-menu more-menu--mobile"
      data-testid="mobile-primary-nav"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="more-menu__backdrop" onClick={onClose} />
      <div className="more-menu__panel" ref={panelRef}>
        <div className="more-menu__header">
          <h2 id={titleId}>Navigate</h2>
          <button type="button" className="btn ghost compact" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="more-menu__groups">
          <section className="more-menu__group" aria-labelledby={`${titleId}-primary`}>
            <h3 id={`${titleId}-primary`}>Primary</h3>
            <ul>
              {DESKTOP_DOCK_ITEMS.map((item) => (
                <li key={item.id}>
                  <PrimaryNavLink
                    item={item}
                    active={isDockDestinationActive(location.pathname, item)}
                    onNavigate={onClose}
                  />
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function PrimaryNavLink({
  item,
  active,
  onNavigate,
}: {
  item: DockDestination;
  active: boolean;
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={`more-menu__link${active ? ' is-active' : ''}`}
      onClick={onNavigate}
      data-availability={item.availability}
      data-testid={`mobile-nav-link-${item.id}`}
      aria-current={active ? 'page' : undefined}
    >
      <Icon />
      <span>{item.label}</span>
    </NavLink>
  );
}
